import { FC, useMemo, useState } from "react";
import { Loader2, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Banner } from "@/components/_atoms/banner";
import { Button } from "@/components/_atoms/button";
import { Checkbox } from "@/components/_atoms/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/_atoms/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/_atoms/table";
import { DeleteConfirmation } from "@/components/_molecules/delete-confirmation";
import { TableEmptyState } from "@/components/_molecules/table-empty-state/table-empty-state";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useWorkspaceTerminology } from "@/domain/workspace/hooks/use-workspace-terminology";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { TrashEntityType } from "@ark/shared";
import { useQueryClient } from "@tanstack/react-query";

import { TrashScope } from "../hooks/api/trash.gql";
import { usePermanentlyDeleteEntities } from "../hooks/api/use-permanently-delete-entities";
import { useRestoreEntities } from "../hooks/api/use-restore-entities";
import { useTrashList } from "../hooks/api/use-trash-list";
import { getDaysRemaining } from "../lib/trash-retention";

const FILTER_ALL = "ALL";
const DAYS_REMAINING_WARNING_THRESHOLD = 5;

interface TrashItemRow {
  id: string;
  entityType: TrashEntityType;
  label: string;
  deletedAt: string;
  deletedByName: string | null;
  canRestore: boolean;
  canPermanentlyDelete: boolean;
}

interface TrashPageProps {
  scope: TrashScope;
  entityType?: TrashEntityType;
  teamId?: string;
}

function formatDate(dateStr: string, language: string): string {
  return new Date(dateStr).toLocaleDateString(language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const TrashPage: FC<TrashPageProps> = ({ scope, entityType, teamId }) => {
  const { t, i18n } = useDomainTranslation("trash");
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const terminology = useWorkspaceTerminology(currentWorkspace.id);
  const queryClient = useQueryClient();

  const { itemList, loading, hasNextPage, hasPreviousPage, nextPage, previousPage } = useTrashList({
    workspaceId: currentWorkspace.id,
    scope,
    entityType,
    teamId,
  });

  const [selectedType, setSelectedType] = useState<string>(FILTER_ALL);
  const [selectedIdSet, setSelectedIdSet] = useState<Set<string>>(new Set());
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const rows: TrashItemRow[] = useMemo(
    () =>
      itemList.map((item) => ({
        id: item.id,
        entityType: item.entityType,
        label: item.label,
        deletedAt: item.deletedAt,
        deletedByName: item.deletedByName ?? null,
        canRestore:
          item.canRestore ??
          !(item.entityType === "municipalPropertyRegister" || item.entityType === "municipalPropertyYear"),
        canPermanentlyDelete:
          item.canPermanentlyDelete ??
          !(item.entityType === "municipalPropertyRegister" || item.entityType === "municipalPropertyYear"),
      })),
    [itemList],
  );

  const availableTypeList = useMemo(() => [...new Set(rows.map((row) => row.entityType))], [rows]);
  const displayedRows = useMemo(
    () => (selectedType === FILTER_ALL ? rows : rows.filter((row) => row.entityType === selectedType)),
    [rows, selectedType],
  );

  const entityTypeById = useMemo(() => new Map(rows.map((row) => [row.id, row.entityType])), [rows]);

  const handleToggleRow = (id: string) => {
    setSelectedIdSet((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectableRowList: TrashItemRow[] = displayedRows.filter(
    (row: TrashItemRow): boolean => row.canRestore || row.canPermanentlyDelete,
  );
  const areAllDisplayedSelected =
    selectableRowList.length > 0 && selectableRowList.every((row) => selectedIdSet.has(row.id));

  const handleToggleAll = () => {
    setSelectedIdSet(areAllDisplayedSelected ? new Set() : new Set(selectableRowList.map((row) => row.id)));
  };

  const clearProcessedFromSelection = (processedIdList: string[]) => {
    const processed = new Set(processedIdList);
    setSelectedIdSet((previous) => new Set([...previous].filter((id) => !processed.has(id))));
  };

  // Errors are surfaced per-group by executeRestore/executePermanentDelete, so
  // no per-hook onError toast here (it would double up on partial failures).
  const { restoreEntities, loading: isRestoring } = useRestoreEntities(currentWorkspace.id);
  const { permanentlyDeleteEntities, loading: isDeleting } = usePermanentlyDeleteEntities(currentWorkspace.id);

  const groupIdsByType = (idList: string[]): Map<string, string[]> => {
    const grouped = new Map<string, string[]>();
    for (const id of idList) {
      const entityType = entityTypeById.get(id);
      if (!entityType) continue;
      grouped.set(entityType, [...(grouped.get(entityType) ?? []), id]);
    }
    return grouped;
  };

  const runGroupedMutation = async ({
    idList,
    mutate,
    onSuccessToast,
    onErrorToast,
  }: {
    idList: string[];
    mutate: (input: { entityType: string; idList: string[] }) => Promise<number>;
    onSuccessToast: (count: number) => void;
    onErrorToast: () => void;
  }) => {
    const grouped = [...groupIdsByType(idList)];
    const results = await Promise.allSettled(
      grouped.map(([entityType, ids]) => mutate({ entityType, idList: ids }).then((count) => ({ ids, count }))),
    );

    let processedCount = 0;
    const processedIdList: string[] = [];
    let failed = false;
    for (const result of results) {
      if (result.status === "fulfilled") {
        processedCount += result.value.count;
        processedIdList.push(...result.value.ids);
      } else {
        failed = true;
      }
    }

    clearProcessedFromSelection(processedIdList);
    queryClient.invalidateQueries({ queryKey: queryKeys.trash.workspaceLists(currentWorkspace.id) });
    if (processedCount > 0) {
      onSuccessToast(processedCount);
    }
    if (failed) {
      onErrorToast();
    }
  };

  const executeRestore = (idList: string[]) =>
    runGroupedMutation({
      idList: idList.filter((id: string): boolean =>
        rows.some((row: TrashItemRow): boolean => row.id === id && row.canRestore),
      ),
      mutate: (input) => restoreEntities(input).then((result) => result.restoredCount),
      onSuccessToast: (count) =>
        toast.success(t("toast.restoredTitle"), { description: t("toast.restoredDescription", { count }) }),
      onErrorToast: () =>
        toast.error(t("toast.restoreErrorTitle"), { description: t("toast.restoreErrorDescription") }),
    });

  const executePermanentDelete = (idList: string[]) =>
    runGroupedMutation({
      idList: idList.filter((id: string): boolean =>
        rows.some((row: TrashItemRow): boolean => row.id === id && row.canPermanentlyDelete),
      ),
      mutate: permanentlyDeleteEntities,
      onSuccessToast: (count) =>
        toast(t("toast.deletedTitle"), { description: t("toast.deletedDescription", { count }) }),
      onErrorToast: () => toast.error(t("toast.deleteErrorTitle"), { description: t("toast.deleteErrorDescription") }),
    });

  const entityTypeLabel = (entityType: TrashEntityType): string => {
    switch (entityType) {
      case "climateAction":
        return terminology.climateAction.plural;
      case "project":
        return terminology.project.plural;
      case "task":
        return terminology.task.plural;
      case "indicator":
        return t("types.indicator");
      case "inventory":
        return t("types.inventory");
      case "municipalProperty":
        return t("types.municipalProperty");
      case "municipalPropertyRegister":
        return t("types.municipalPropertyRegister");
      case "municipalPropertyYear":
        return t("types.municipalPropertyYear");
      case "scenarioModel":
        return t("types.scenarioModel");
      case "dataRequest":
        return t("types.dataRequest");
      default:
        return entityType;
    }
  };

  const isBusy = isRestoring || isDeleting;
  const selectedIdList = Array.from(selectedIdSet);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="flex h-full max-h-full flex-col overflow-y-auto p-4">
      <Banner variant="info" className="mb-4">
        {t("description")}
      </Banner>

      {(selectedIdSet.size > 0 || availableTypeList.length > 1) && (
        <div className="mb-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {selectedIdSet.size > 0 && (
              <>
                <span className="text-xs text-muted-foreground">
                  {t("selectedCount", { count: selectedIdSet.size })}
                </span>
                <Button
                  size="xs"
                  variant="outline"
                  disabled={isBusy || !rows.some((row) => selectedIdSet.has(row.id) && row.canRestore)}
                  onClick={() => executeRestore(selectedIdList)}
                >
                  <RotateCcw className="mr-1 h-3 w-3" />
                  {t("actions.restoreSelected")}
                </Button>
                <Button
                  size="xs"
                  variant="destructive"
                  disabled={isBusy || !rows.some((row) => selectedIdSet.has(row.id) && row.canPermanentlyDelete)}
                  onClick={() => setIsConfirmOpen(true)}
                >
                  <Trash2 className="mr-1 h-3 w-3" />
                  {t("actions.permanentlyDeleteSelected")}
                </Button>
              </>
            )}
          </div>
          {availableTypeList.length > 1 && (
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FILTER_ALL}>{t("filter.allTypes")}</SelectItem>
                {availableTypeList.map((entityType) => (
                  <SelectItem key={entityType} value={entityType}>
                    {entityTypeLabel(entityType)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {displayedRows.length === 0 ? (
        <TableEmptyState
          icon={<Trash2 className="h-6 w-6" aria-hidden="true" />}
          title={t("emptyTitle")}
          description={t("emptyDescription")}
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  disabled={selectableRowList.length === 0}
                  checked={areAllDisplayedSelected}
                  onCheckedChange={handleToggleAll}
                  aria-label={t("actions.selectAll")}
                />
              </TableHead>
              <TableHead>{t("columns.label")}</TableHead>
              <TableHead>{t("columns.type")}</TableHead>
              <TableHead>{t("columns.deletedAt")}</TableHead>
              <TableHead>{t("columns.deletedBy")}</TableHead>
              <TableHead>{t("columns.daysRemaining")}</TableHead>
              <TableHead className="text-right">{t("columns.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayedRows.map((row) => {
              const daysRemaining = getDaysRemaining(row.deletedAt);
              const isSelected = selectedIdSet.has(row.id);
              return (
                <TableRow key={row.id} data-state={isSelected ? "selected" : undefined}>
                  <TableCell>
                    <Checkbox
                      disabled={!row.canRestore && !row.canPermanentlyDelete}
                      checked={isSelected}
                      onCheckedChange={() => handleToggleRow(row.id)}
                      aria-label={row.label}
                    />
                  </TableCell>
                  <TableCell className="font-medium text-foreground">{row.label}</TableCell>
                  <TableCell className="text-muted-foreground">{entityTypeLabel(row.entityType)}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(row.deletedAt, i18n.language)}</TableCell>
                  <TableCell className="text-muted-foreground">{row.deletedByName ?? t("unknownUser")}</TableCell>
                  <TableCell>
                    <span
                      className={
                        daysRemaining <= DAYS_REMAINING_WARNING_THRESHOLD
                          ? "font-medium text-destructive"
                          : "text-muted-foreground"
                      }
                    >
                      {daysRemaining} {daysRemaining === 1 ? t("daySingular") : t("dayPlural")}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {row.canRestore && (
                        <Button size="xs" variant="outline" disabled={isBusy} onClick={() => executeRestore([row.id])}>
                          <RotateCcw className="mr-1 h-3 w-3" />
                          {t("actions.restore")}
                        </Button>
                      )}
                      {row.canPermanentlyDelete && (
                        <Button
                          size="xs"
                          variant="destructive"
                          disabled={isBusy}
                          onClick={() => {
                            setSelectedIdSet(new Set([row.id]));
                            setIsConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="mr-1 h-3 w-3" />
                          {t("actions.permanentlyDelete")}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {(hasNextPage || hasPreviousPage) && (
        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="outline"
            disabled={!hasPreviousPage || isBusy}
            onClick={() => {
              setSelectedIdSet(new Set());
              setSelectedType(FILTER_ALL);
              previousPage();
            }}
          >
            {t("actions.previousPage")}
          </Button>
          <Button
            variant="outline"
            disabled={!hasNextPage || isBusy}
            onClick={() => {
              setSelectedIdSet(new Set());
              setSelectedType(FILTER_ALL);
              nextPage();
            }}
          >
            {t("actions.nextPage")}
          </Button>
        </div>
      )}

      <DeleteConfirmation
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={t("confirmDelete.title")}
        description={t("confirmDelete.description")}
        onConfirm={() => {
          setIsConfirmOpen(false);
          void executePermanentDelete(selectedIdList);
        }}
        onCancel={() => setIsConfirmOpen(false)}
      />
    </div>
  );
};
